import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import type { Pluggable } from 'unified';
import cn from '@/utils/cn';
import SPAPageHeader from '../shared/SPAPageHeader';
import { humanizeFilename } from './devDocs.utils';
import languages from './highlightLanguages';
import 'highlight.js/styles/github-dark-dimmed.css';

interface MarkdownViewerProps {
  content: string;
  /** Repo path of the file — shown under the header, and the title fallback. */
  path?: string;
}

// AIDEV-NOTE: remark-gfm enables task list checkboxes (- [ ] / - [x]) and tables.
// rehype-highlight provides syntax highlighting via highlight.js (github-dark-dimmed theme).
// Component keys must be alphabetically sorted (eslint sort-keys).
// Colors use SPA theme tokens — adapts to light/dark via prefers-color-scheme.
const markdownComponents = {
  a: ({ children, href }: { children: React.ReactNode; href?: string }) => (
    <a
      className="tw-text-spa-accent-text tw-underline hover:tw-opacity-80"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </a>
  ),
  // AIDEV-NOTE: react-markdown v8 passes `inline` to distinguish `code` from fenced code blocks.
  // Previously only checked className, so blocks without a language lost their <pre> wrapper.
  code: ({ children, className: codeClassName, inline }: {
    children: React.ReactNode; className?: string; inline?: boolean;
  }) => {
    if (!inline) {
      return (
        <pre
          className={cn(
            'tw-bg-spa-bg-code tw-border tw-border-solid tw-border-spa-border-secondary tw-rounded-md',
            'tw-px-5 tw-py-4 tw-overflow-x-auto tw-my-3',
            'tw-font-mono tw-text-[13px] tw-leading-normal',
          )}
        >
          <code className={codeClassName}>{children}</code>
        </pre>
      );
    }
    return (
      <code
        className={cn(
          'tw-bg-spa-bg-tertiary tw-border tw-border-solid tw-border-spa-border-input tw-rounded',
          'tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-[0.875em] tw-text-spa-accent-text-hover',
        )}
      >
        {children}
      </code>
    );
  },
  h1: ({ children }: { children: React.ReactNode }) => (
    <h1
      className={cn(
        'tw-mt-7 tw-mb-3 tw-pb-2 tw-text-2xl tw-font-semibold tw-text-spa-heading',
        'tw-border-b-2 tw-border-solid tw-border-spa-accent first:tw-mt-0',
      )}
    >
      {children}
    </h1>
  ),
  h2: ({ children }: { children: React.ReactNode }) => (
    <h2
      className={cn(
        'tw-mt-6 tw-mb-2.5 tw-pb-1.5 tw-text-xl tw-font-semibold tw-text-spa-on-bg',
        'tw-border-b tw-border-solid tw-border-spa-border first:tw-mt-0',
      )}
    >
      {children}
    </h2>
  ),
  h3: ({ children }: { children: React.ReactNode }) => (
    <h3 className="tw-mt-5 tw-mb-2 tw-text-lg tw-font-semibold tw-text-spa-on-bg first:tw-mt-0">
      {children}
    </h3>
  ),
  li: ({ children }: { children: React.ReactNode }) => (
    <li className="tw-mb-1.5 tw-leading-relaxed">{children}</li>
  ),
  ol: ({ children }: { children: React.ReactNode }) => (
    <ol className="tw-my-2 tw-mb-4 tw-pl-6 tw-list-decimal tw-list-outside">{children}</ol>
  ),
  p: ({ children }: { children: React.ReactNode }) => (
    <p className="tw-mt-0 tw-mb-3 last:tw-mb-0">{children}</p>
  ),
  pre: ({ children }: { children: React.ReactNode }) => <>{children}</>,
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="tw-font-semibold tw-text-spa-heading">{children}</strong>
  ),
  table: ({ children }: { children: React.ReactNode }) => (
    <div className="tw-overflow-x-auto tw-my-4">
      <table
        className={cn(
          'tw-border-collapse tw-text-sm',
          'tw-border tw-border-solid tw-border-spa-border',
        )}
      >
        {children}
      </table>
    </div>
  ),
  td: ({ children }: { children: React.ReactNode }) => (
    <td
      className={cn(
        'tw-px-4 tw-py-2.5 tw-border tw-border-solid tw-border-spa-border',
        'tw-text-spa-primary',
      )}
    >
      {children}
    </td>
  ),
  th: ({ children }: { children: React.ReactNode }) => (
    <th
      className={cn(
        'tw-px-4 tw-py-2.5 tw-border tw-border-solid tw-border-spa-border',
        'tw-bg-spa-bg-secondary tw-text-spa-on-bg tw-font-semibold tw-text-left',
      )}
    >
      {children}
    </th>
  ),
  ul: ({ children }: { children: React.ReactNode }) => (
    <ul className="tw-my-2 tw-mb-4 tw-pl-6 tw-list-disc">{children}</ul>
  ),
};

// AIDEV-NOTE: Parses YAML frontmatter (---\n...\n---) into key-value pairs and body content.
const parseFrontmatter = (text: string): { body: string; fields: Record<string, string> } => {
  const match = text.match(/^---\n([\s\S]*?)\n---\n*([\s\S]*)$/);
  if (!match) return { body: text, fields: {} };

  const fields: Record<string, string> = {};
  match[1].split('\n').forEach((line) => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      fields[line.slice(0, colonIndex).trim()] = line.slice(colonIndex + 1).trim();
    }
  });
  return { body: match[2], fields };
};

// A leading `# Title` becomes the page header instead of repeating as a second heading below it.
const splitLeadingHeading = (body: string): { heading: string | null; rest: string } => {
  const match = body.match(/^\s*#[ \t]+([^\n]+)\n?([\s\S]*)$/);
  return match ? { heading: match[1].trim(), rest: match[2] } : { heading: null, rest: body };
};

const MetaChips = ({ meta }: { meta: [string, string][] }) => (
  <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-spa-tertiary">
    {meta.map(([key, value]) => (
      <span key={key}>
        <span className="tw-font-mono tw-text-spa-accent-text">{key}</span>
        {' '}
        {value}
      </span>
    ))}
  </div>
);

const MarkdownViewer = ({ content, path }: MarkdownViewerProps) => {
  const { fields, body } = parseFrontmatter(content);
  const { heading, rest } = splitLeadingHeading(body);
  const { description, name, ...otherFields } = fields;
  const title = heading ?? name ?? (path ? humanizeFilename(path) : 'Untitled');
  const showPath = Boolean(path) && path !== title;
  const meta: [string, string][] = [
    ...(name && name !== title ? [['name', name] as [string, string]] : []),
    ...Object.entries(otherFields),
  ];

  return (
    <div>
      <SPAPageHeader title={title} />
      {(description || showPath || meta.length > 0) && (
        <div className="tw-mb-5 tw-flex tw-flex-col tw-gap-1.5">
          {description && <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">{description}</p>}
          {showPath && <p className="tw-m-0 tw-text-xs tw-font-mono tw-text-spa-tertiary">{path}</p>}
          {meta.length > 0 && <MetaChips meta={meta} />}
        </div>
      )}
      <div className="tw-leading-[1.7] tw-text-spa-primary">
        <ReactMarkdown
          components={markdownComponents}
          // AIDEV-NOTE: Cast to Pluggable needed — react-markdown v8 PluggableList types
          // don't align with rehype-highlight v7's Plugin signature (TS 4.6 unified mismatch).
          rehypePlugins={[[rehypeHighlight, { languages }] as Pluggable]}
          remarkPlugins={[remarkGfm]}
        >
          {rest}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default MarkdownViewer;
